import { useState } from 'react';
import { FiMail } from 'react-icons/fi';
import { FormModal, FormField, Input } from '../ui';
import { Badge } from '../ui/Badge/Badge';
import styles from './InviteModal.module.css';

interface InviteFormData {
  email: string;
  role: 'member' | 'admin' | 'viewer';
}

interface InviteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: InviteFormData) => Promise<void>;
  isLoading?: boolean;
  error?: string | null;
}

export function InviteModal({ isOpen, onClose, onSubmit, isLoading = false, error = null }: InviteModalProps) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'member' | 'admin' | 'viewer'>('member');
  const [errors, setErrors] = useState<{ email?: string }>({});

  const validateEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const handleClose = () => {
    setEmail('');
    setRole('member');
    setErrors({});
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isLoading) return;
    const newErrors: { email?: string } = {};
    if (!email.trim()) newErrors.email = 'Email is required';
    else if (!validateEmail(email)) newErrors.email = 'Please enter a valid email address';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      await onSubmit({ email, role });
      handleClose();
    } catch {
      // Error handling is done by parent component
    }
  };

  const getRoleBadgeVariant = (roleOption: string): 'primary' | 'secondary' | 'info' | 'tertiary' => {
    switch (roleOption) {
      case 'admin': return 'info';
      case 'member': return 'secondary';
      case 'viewer': return 'tertiary';
      default: return 'secondary';
    }
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Invite Team Member"
      size="medium"
      onSubmit={handleSubmit}
      submitLabel={isLoading ? 'Sending...' : 'Send Invitation'}
      isSubmitting={isLoading}
    >
      {error && (
        <div className={styles.errorAlert}>
          <span className={styles.errorIcon}>⚠️</span>
          <span className={styles.errorMessage}>{error}</span>
        </div>
      )}

      <FormField label="Email Address" htmlFor="invite-email" required error={errors.email}>
        <div className={styles.inputWrapper}>
          <FiMail className={styles.inputIcon} />
          <Input
            id="invite-email"
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setErrors({ ...errors, email: undefined });
            }}
            placeholder="colleague@example.com"
            disabled={isLoading}
            error={!!errors.email}
            fullWidth
          />
        </div>
      </FormField>

      <FormField label="Role" htmlFor="invite-role-group">
        <div className={styles.roleOptions} id="invite-role-group">
          {(['member', 'admin', 'viewer'] as const).map((roleOption) => (
            <label key={roleOption} className={styles.roleOption}>
              <input
                type="radio"
                name="role"
                value={roleOption}
                checked={role === roleOption}
                onChange={(e) => setRole(e.target.value as 'member' | 'admin' | 'viewer')}
                disabled={isLoading}
                className={styles.radioInput}
              />
              <div className={styles.roleCard}>
                <Badge variant={getRoleBadgeVariant(roleOption)} size="sm">
                  {roleOption}
                </Badge>
                <p className={styles.roleDescription}>
                  {roleOption === 'admin' && 'Can manage members and settings'}
                  {roleOption === 'member' && 'Can access and use features'}
                  {roleOption === 'viewer' && 'Read-only access'}
                </p>
              </div>
            </label>
          ))}
        </div>
      </FormField>
    </FormModal>
  );
}

